/**
 * =========================================================================================
 *   Copyright © 2026 Dobler Data Solutions, LLC. ALL RIGHTS RESERVED.
 * =========================================================================================
 *   Module       : api/backupErrors
 *   Feature      : 252 - Backup Error Visibility & Guided Remediation
 *   Description  : Typed client for the Backup Errors read API — the actual error DETAIL plus an
 *                  attributed cause and an ordered, client-actionable remediation.
 *   Author       : Peter Dobler (Dobler Data Solutions)
 * =========================================================================================
 */
import { apiClient } from '@/services/apiClient';

export type CauseClass =
  | 'platform_limitation'
  | 'configuration'
  | 'data_issue'
  | 'resource_scale'
  | 'transient'
  | 'product_defect'
  | 'needs_triage';

export type ErrorStatus = 'failing' | 'at_risk' | 'resolved' | 'accepted_limitation';

export interface RemediationStep {
  n: number;
  text: string;
  clientActionable: boolean;
  lastAttempt?: { outcome: string; by: string | null; at: string; note: string | null };
}

export interface ResolvedRemediation {
  /** Which catalog resolved this code — the A2 asymmetry is visible, not silent. */
  source: 'error_catalog' | 'readiness_reason_codes' | 'synthesized';
  title: string;
  explanation: string;
  clientActionable: boolean;
  docsUrl: string | null;
  steps: RemediationStep[];
}

export interface BackupErrorItem {
  errorKey: string;
  surface: string;
  entityType: string | null;
  entityId: string | null;
  entityName: string | null;
  runId: string | null;
  occurredAt: string | null;
  status: ErrorStatus;
  /** Exactly what the database holds — the error key is built from this, so it round-trips. */
  storedCode: string;
  /** The code the remediation on screen actually came from. Differs when a placeholder was resolved. */
  resolvedCode: string;
  causeClass: CauseClass;
  severity: string;
  title: string;
  detailPreview: string | null;
  isAcceptedLimitation: boolean;
  attemptCount: number;
  firstSeen: string | null;
  lastSeen: string | null;
  objectName: string | null;
}

export interface ErrorGroup {
  causeClass: CauseClass;
  code: string;
  title: string;
  severity: string;
  isAcceptedLimitation: boolean;
  affectedCount: number;
  entities: Array<{
    entityType: string | null; entityId: string | null; entityName: string | null;
    lastSeen: string | null; errorKey: string;
    /** so a click from the grouped view opens THAT entity's run, not the problem's newest one */
    runId: string | null;
    /** required to distinguish one code hitting the same entity on two surfaces */
    surface: string;
  }>;
}

export interface ErrorListResponse {
  total: number;
  page: number;
  pageSize: number;
  group: 'cause' | 'none';
  groups?: ErrorGroup[];
  items: BackupErrorItem[];
}

export interface BackupErrorDetail extends BackupErrorItem {
  rawDetail: string | null;
  occurrences: Array<{ runId: string | null; occurredAt: string | null; status: string | null }>;
  affectedObjects: Array<{ name: string; type: string; status: string; detail: string | null; occurrences: number }>;
  remediation: ResolvedRemediation;
  trail: Array<{ stepIndex: number; outcome: string; by: string | null; at: string; note: string | null }>;
  escalated: boolean;
}

export interface ErrorFilters {
  surface?: string[];
  entityType?: string[];
  /** EXACT entity match — what an alert deep-link carries. NOT `q`, which is a LIKE a GUID fails. */
  entityId?: string[];
  causeClass?: string[];
  severity?: string[];
  status?: string[];
  q?: string;
  from?: string;
  to?: string;
  group?: 'cause' | 'none';
  page?: number;
  pageSize?: number;
}

type Wrapped<T> = { success: boolean; data: T };

const BASE = '/api/v1/backup/errors';

function qs(f: ErrorFilters): string {
  const p = new URLSearchParams();
  const csv = (k: string, v?: string[]) => { if (v?.length) p.set(k, v.join(',')); };
  csv('surface', f.surface);
  csv('entityType', f.entityType);
  csv('entityId', f.entityId);
  csv('causeClass', f.causeClass);
  csv('severity', f.severity);
  csv('status', f.status);
  if (f.q) p.set('q', f.q);
  if (f.from) p.set('from', f.from);
  if (f.to) p.set('to', f.to);
  if (f.group) p.set('group', f.group);
  if (f.page) p.set('page', String(f.page));
  if (f.pageSize) p.set('pageSize', String(f.pageSize));
  const s = p.toString();
  return s ? `?${s}` : '';
}

export const backupErrorsApi = {
  /** Paged, filtered error list. Grouped by root cause by default (FR-010). */
  list: async (f: ErrorFilters = {}): Promise<ErrorListResponse> =>
    (await apiClient.get<Wrapped<ErrorListResponse>>(`${BASE}${qs(f)}`)).data.data,

  /** Counts by cause class / severity / surface. */
  summary: async (f: ErrorFilters = {}): Promise<Record<string, Record<string, number>>> =>
    (await apiClient.get<Wrapped<Record<string, Record<string, number>>>>(`${BASE}/summary${qs(f)}`)).data.data,

  /**
   * Full detail + resolved remediation for one error (FR-002/003/005).
   * @param runId pin the display to the run the operator clicked; omit to show the latest
   *   occurrence (correct for an alert deep-link, which knows the entity but not the run).
   */
  detail: async (errorKey: string, runId?: string | null): Promise<BackupErrorDetail> =>
    (await apiClient.get<Wrapped<BackupErrorDetail>>(
      `${BASE}/${encodeURIComponent(errorKey)}${runId ? `?runId=${encodeURIComponent(runId)}` : ''}`)).data.data,

  /** Record ONE remediation step as attempted (FR-011 — per step, not per error). */
  recordAttempt: async (
    errorKey: string,
    body: { catalogCode: string; stepIndex: number; outcome: 'attempted' | 'succeeded' | 'failed' | 'waived'; note?: string },
  ): Promise<unknown> =>
    (await apiClient.post<Wrapped<unknown>>(`${BASE}/${encodeURIComponent(errorKey)}/attempts`, body)).data.data,

  /**
   * Escalate (FR-012). Throws with code REMEDIATION_INCOMPLETE (HTTP 409) while client-actionable
   * steps remain un-attempted and un-waived, unless acknowledgeUnattempted is set.
   * v1 records IN-PRODUCT ONLY — no email/Teams/Jira dispatch.
   */
  escalate: async (errorKey: string, body: { note?: string; acknowledgeUnattempted?: boolean } = {}): Promise<unknown> =>
    (await apiClient.post<Wrapped<unknown>>(`${BASE}/${encodeURIComponent(errorKey)}/escalate`, body)).data.data,

  /** The merged catalog; missingOnly=true is the SC-002 oracle (must always be empty). */
  catalog: async (missingOnly = false): Promise<{ items: Array<Record<string, unknown>> }> =>
    (await apiClient.get<Wrapped<{ items: Array<Record<string, unknown>> }>>(
      `${BASE}/catalog${missingOnly ? '?missingOnly=true' : ''}`)).data.data,
};

export default backupErrorsApi;
